import cx from 'classnames';
import { useCallback } from 'react';
import type { FunctionComponent } from 'react';

import BoardsIcon from '@atlaskit/icon/core/boards';
import { Analytics } from '@trello/atlassian-analytics';
import {
  boardSwitcherSharedState,
  useBoardSwitcherMode,
} from '@trello/board-switcher';
import { intl } from '@trello/i18n';
import { getScreenFromUrl } from '@trello/marketing-screens';
import { useSharedState } from '@trello/shared-state';
import {
  useIsBoardPanelOpen,
  useIsInboxPanelOpen,
  useIsPlannerPanelOpen,
  useSplitScreenSharedState,
} from '@trello/split-screen';
import { getTestId, type BoardSwitcherTestIds } from '@trello/test-ids';

import { IslandNavButton } from 'app/src/components/IslandNav';
import { LazySpotlightPulse } from 'app/src/components/Onboarding';
import { useBoardSwitcherButtonPulseAndSpotlight } from 'app/src/components/PersonalProductivityOnboarding/useBoardSwitcherButtonPulseAndSpotlight';
import { ShortcutTooltip } from 'app/src/components/ShortcutTooltip';

import * as styles from './BoardSwitcherButton.module.less';

interface BoardSwitcherButtonProps {
  showLabels?: boolean;
  isDisabled?: boolean;
}

export const BoardSwitcherButton: FunctionComponent<
  BoardSwitcherButtonProps
> = ({ showLabels = false, isDisabled = false }) => {
  const isInboxOpen = useIsInboxPanelOpen();
  const isPlannerOpen = useIsPlannerPanelOpen();
  const isBoardOpen = useIsBoardPanelOpen();

  const { shouldShowSpotlight } = useBoardSwitcherButtonPulseAndSpotlight();

  const boardSwitcherMode = useBoardSwitcherMode();

  const {
    configuration: splitScreenConfiguration,
    panels,
    toggleInbox,
    toggleBoard,
    togglePlanner,
  } = useSplitScreenSharedState();

  const [{ isOpen: isBoardSwitcherOpen, layout }] = useSharedState(
    boardSwitcherSharedState,
  );

  const onClickHandler = useCallback(async () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'boardSwitcherButton',
      source: getScreenFromUrl(),
      attributes: {
        isInboxOpen,
        isPlannerOpen,
        isBoardOpen,
        layout,
      },
    });

    if (boardSwitcherMode === 'panel') {
      boardSwitcherSharedState.setValue({
        isOpen: !isBoardSwitcherOpen,
      });

      if (!isBoardSwitcherOpen && splitScreenConfiguration === 'tabs') {
        if (panels.inbox) {
          toggleInbox();
        } else if (panels.planner) {
          togglePlanner();
        } else if (panels.board) {
          toggleBoard();
        }
      }
    } else {
      boardSwitcherSharedState.setValue({
        isOpen: true,
      });
    }
  }, [
    isBoardOpen,
    isBoardSwitcherOpen,
    boardSwitcherMode,
    isInboxOpen,
    isPlannerOpen,
    layout,
    panels.board,
    panels.inbox,
    panels.planner,
    splitScreenConfiguration,
    toggleBoard,
    toggleInbox,
    togglePlanner,
  ]);

  const title = intl.formatMessage({
    id: 'templates.split_screen.switch-boards',
    defaultMessage: 'Switch boards',
    description: 'Switch boards',
  });

  const button = (
    <ShortcutTooltip shortcutText={title} shortcutKey="b" position="top">
      <IslandNavButton
        icon={<BoardsIcon label={showLabels ? '' : title} />}
        onClick={onClickHandler}
        testId={getTestId<BoardSwitcherTestIds>(
          'panel-nav-board-switcher-button',
        )}
        showLabels={showLabels}
        label={title}
        isDisabled={isDisabled}
        isSelected={boardSwitcherMode === 'panel' && isBoardSwitcherOpen}
      />
    </ShortcutTooltip>
  );

  return (
    <div
      className={cx(styles.boardSwitcherButton, {
        [styles.withLabel]: showLabels,
      })}
    >
      {shouldShowSpotlight ? (
        <LazySpotlightPulse radius={3}>{button}</LazySpotlightPulse>
      ) : (
        button
      )}
    </div>
  );
};
